// react and js
import PropTypes from "prop-types";
import * as Yup from "yup";
import { React, useState, useRef, useEffect, useContext } from "react";
import { useFormik, FormikProvider } from "formik";
import { Trans, useTranslation } from "react-i18next";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Autocomplete,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import backendRequest from "../../../components/BackendRequest";

// hooks
import appContext from "../../../hooks/appContext";

// layouts

// pages

// theme
import {
  B3Tiny,
  ModalContentTextField,
  ModalActionsLoadingButton,
  AutocompleteOption,
} from "../../../theme/styled";

// utils
import { combineReturnMessage, TranslatedMessage } from "../../../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------

function RequestBackendAction(
  setSubmitting,
  setError,
  setSuccess,
  setWarning,
  setReturnMessage,
  postRequestUpdate,
  SubmitRequestContent
) {
  if (
    localStorage.getItem("myReauthentication") === "false" &&
    navigator.onLine === true
  ) {
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");

    backendRequest(
      process.env.REACT_APP_URL_API_DASHBOARD,
      SubmitRequestContent
    )
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("message");

        if (checkKey === true) {
          if (contentFromBackend.message === "success") {
            setSuccess(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
            postRequestUpdate(750);
          } else {
            setWarning(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
          }
        } else {
          setWarning(true);
          combineReturnMessage(contentFromBackend, setReturnMessage);
        }
        setSubmitting(false);
      })
      .catch(() => {
        if (navigator.onLine === true) {
          setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
          setError(true);
          setSubmitting(false);
        }
      });
  }
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandleDistributorPortalModifyPlanLimitations({
  distributor,
  permissionId,
  plan,
}) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandleDistributorPortalModifyPlanLimitations.propTypes = {
    distributor: PropTypes.any.isRequired,
    permissionId: PropTypes.any.isRequired,
    plan: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const wrapperRef = useRef(null);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const postRequestUpdate = (autoClose) => {
    setTimeout(() => {
      handleClose();
      setStateStorage((previousState) => ({
        ...previousState,
        widgetUpdateDistributorPortalManageDistributionCustomer: true,
      }));
      setError(false);
      setSuccess(false);
      setWarning(false);
    }, autoClose);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setError(false);
        setWarning(false);
        setSuccess(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, {
      passive: false,
      capture: false,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const limitationRegExp = /^[0-9]|Plan default value|Unlimited+$/;
  const limitationDisableRegExp = /^[0-9]|Plan default value|Disabled+$/;

  const myValidationScheme = Yup.object().shape({
    permission_max_sessions: Yup.string()
      .required()
      .matches(limitationRegExp),
    permission_min_application_equity: Yup.string()
      .required()
      .matches(limitationDisableRegExp),
    permission_live_included_accounts: Yup.string()
      .required()
      .matches(limitationRegExp),
    permission_live_included_account_changes: Yup.string()
      .required()
      .matches(limitationRegExp),
    permission_demo_included_accounts: Yup.string()
      .required()
      .matches(limitationRegExp),
    permission_demo_included_account_changes: Yup.string()
      .required()
      .matches(limitationRegExp),
  });

  let formMaxSessionsInitValue = "Plan default value";
  if (
    plan.permission_max_sessions === null ||
    plan.permission_max_sessions === undefined ||
    plan.permission_max_sessions === "" ||
    String(plan.permission_max_sessions) === "-1"
  ) {
    // Treat legacy "-1" (Unlimited) as plan default — max sessions must be
    // a finite cap, so "Unlimited" is no longer offered for this field.
    formMaxSessionsInitValue = "Plan default value";
  } else {
    formMaxSessionsInitValue = String(plan.permission_max_sessions);
  }

  // Subscription-wide min equity override (cascades to every permission row
  // sharing the same distribution_linking_token, same pattern as max_sessions).
  // Note: the API exposes this on the plan as `permission_min_equity` (short
  // name); the DB column / POST payload field is `permission_min_application_equity`.
  let formMinApplicationEquityInitValue = "Plan default value";
  if (
    plan.permission_min_equity === null ||
    plan.permission_min_equity === undefined ||
    plan.permission_min_equity === ""
  ) {
    formMinApplicationEquityInitValue = "Plan default value";
  } else if (String(plan.permission_min_equity) === "-1") {
    formMinApplicationEquityInitValue = "Disabled";
  } else {
    formMinApplicationEquityInitValue = String(plan.permission_min_equity);
  }

  let formLiveIncludedAccountsInitValue = "Plan default value";
  if (plan.live_permission_included_accounts === null) {
    formLiveIncludedAccountsInitValue = "Plan default value";
  } else if (plan.live_permission_included_accounts === "-1") {
    formLiveIncludedAccountsInitValue = "Unlimited";
  } else {
    formLiveIncludedAccountsInitValue = plan.live_permission_included_accounts;
  }

  let formLiveIncludedAccountChangesInitValue = "Plan default value";
  if (plan.live_permission_included_account_changes === null) {
    formLiveIncludedAccountChangesInitValue = "Plan default value";
  } else if (plan.live_permission_included_account_changes === "-1") {
    formLiveIncludedAccountChangesInitValue = "Unlimited";
  } else {
    formLiveIncludedAccountChangesInitValue =
      plan.live_permission_included_account_changes;
  }

  let formDemoIncludedAccountsInitValue = "Plan default value";
  if (plan.demo_permission_included_accounts === null) {
    formDemoIncludedAccountsInitValue = "Plan default value";
  } else if (plan.demo_permission_included_accounts === "-1") {
    formDemoIncludedAccountsInitValue = "Unlimited";
  } else {
    formDemoIncludedAccountsInitValue = plan.demo_permission_included_accounts;
  }

  let formDemoIncludedAccountChangesInitValue = "Plan default value";
  if (plan.demo_permission_included_account_changes === null) {
    formDemoIncludedAccountChangesInitValue = "Plan default value";
  } else if (plan.demo_permission_included_account_changes === "-1") {
    formDemoIncludedAccountChangesInitValue = "Unlimited";
  } else {
    formDemoIncludedAccountChangesInitValue =
      plan.demo_permission_included_account_changes;
  }

  const [formMaxSessions, setFormMaxSessions] = useState(
    formMaxSessionsInitValue
  );
  const [formMinApplicationEquity, setFormMinApplicationEquity] = useState(
    formMinApplicationEquityInitValue
  );
  const [formLiveIncludedAccounts, setFormLiveIncludedAccounts] = useState(
    formLiveIncludedAccountsInitValue
  );
  const [formLiveIncludedAccountChanges, setFormLiveIncludedAccountChanges] =
    useState(formLiveIncludedAccountChangesInitValue);
  const [formDemoIncludedAccounts, setFormDemoIncludedAccounts] = useState(
    formDemoIncludedAccountsInitValue
  );
  const [formDemoIncludedAccountChanges, setFormDemoIncludedAccountChanges] =
    useState(formDemoIncludedAccountChangesInitValue);

  const formik = useFormik({
    initialValues: {
      permission_max_sessions: formMaxSessionsInitValue,
      permission_min_application_equity: formMinApplicationEquityInitValue,
      permission_live_included_accounts: formLiveIncludedAccountsInitValue,
      permission_live_included_account_changes:
        formLiveIncludedAccountChangesInitValue,
      permission_demo_included_accounts: formDemoIncludedAccountsInitValue,
      permission_demo_included_account_changes:
        formDemoIncludedAccountChangesInitValue,
    },
    validationSchema: myValidationScheme,
    onSubmit: (values, { setSubmitting }) => {
      let permissionMaxSessionsConverted = values.permission_max_sessions;
      if (permissionMaxSessionsConverted === "Plan default value") {
        permissionMaxSessionsConverted = "plan_default";
      }

      let permissionMinApplicationEquityConverted =
        values.permission_min_application_equity;
      if (permissionMinApplicationEquityConverted === "Plan default value") {
        permissionMinApplicationEquityConverted = "plan_default";
      } else if (permissionMinApplicationEquityConverted === "Disabled") {
        permissionMinApplicationEquityConverted = "disabled";
      }

      let permissionLiveIncludedAccountsProcessedConverted =
        values.permission_live_included_accounts;
      if (
        permissionLiveIncludedAccountsProcessedConverted ===
        "Plan default value"
      ) {
        permissionLiveIncludedAccountsProcessedConverted = "plan_default";
      } else if (
        permissionLiveIncludedAccountsProcessedConverted === "Unlimited"
      ) {
        permissionLiveIncludedAccountsProcessedConverted = "unlimited";
      }

      let permissionLiveIncludedAccountChangesConverted =
        values.permission_live_included_account_changes;
      if (
        permissionLiveIncludedAccountChangesConverted === "Plan default value"
      ) {
        permissionLiveIncludedAccountChangesConverted = "plan_default";
      } else if (
        permissionLiveIncludedAccountChangesConverted === "Unlimited"
      ) {
        permissionLiveIncludedAccountChangesConverted = "unlimited";
      }

      let permissionDemoIncludedAccountsConverted =
        values.permission_demo_included_accounts;
      if (permissionDemoIncludedAccountsConverted === "Plan default value") {
        permissionDemoIncludedAccountsConverted = "plan_default";
      } else if (permissionDemoIncludedAccountsConverted === "Unlimited") {
        permissionDemoIncludedAccountsConverted = "unlimited";
      }

      let permissionDemoIncludedAccountChangesConverted =
        values.permission_demo_included_account_changes;
      if (
        permissionDemoIncludedAccountChangesConverted === "Plan default value"
      ) {
        permissionDemoIncludedAccountChangesConverted = "plan_default";
      } else if (
        permissionDemoIncludedAccountChangesConverted === "Unlimited"
      ) {
        permissionDemoIncludedAccountChangesConverted = "unlimited";
      }

      const SubmitRequestContent = {
        request_type: "dashboard",
        route_info: "distributor_portal",
        widget: "customer_and_account_management",
        operation: "modify_plan_limitations",
        client: {
          dashboard_access_token:
            stateStorage.userInformation.answer.dashboard_access_token,
        },
        admin: {
          selected_distributor_id: distributor,
        },
        permission: {
          selected_permission_id: permissionId,
        },
        limits: {
          permission_max_sessions: permissionMaxSessionsConverted,
          permission_min_application_equity:
            permissionMinApplicationEquityConverted,
          permission_live_included_accounts:
            permissionLiveIncludedAccountsProcessedConverted,
          permission_live_included_account_changes:
            permissionLiveIncludedAccountChangesConverted,
          permission_demo_included_accounts:
            permissionDemoIncludedAccountsConverted,
          permission_demo_included_account_changes:
            permissionDemoIncludedAccountChangesConverted,
        },
      };

      RequestBackendAction(
        setSubmitting,
        setError,
        setSuccess,
        setWarning,
        setReturnMessage,
        postRequestUpdate,
        SubmitRequestContent
      );
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const [scroll, setScroll] = useState("paper");

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);

    // Reinit form values when reopened
    formik.values.permission_max_sessions = formMaxSessionsInitValue;
    formik.values.permission_min_application_equity =
      formMinApplicationEquityInitValue;
    formik.values.permission_live_included_accounts =
      formLiveIncludedAccountsInitValue;
    formik.values.permission_live_included_account_changes =
      formLiveIncludedAccountChangesInitValue;
    formik.values.permission_demo_included_accounts =
      formDemoIncludedAccountsInitValue;
    formik.values.permission_demo_included_account_changes =
      formDemoIncludedAccountChangesInitValue;

    setFormMaxSessions(formMaxSessionsInitValue);
    setFormMinApplicationEquity(formMinApplicationEquityInitValue);
    setFormLiveIncludedAccounts(formLiveIncludedAccountsInitValue);
    setFormLiveIncludedAccountChanges(formLiveIncludedAccountChangesInitValue);
    setFormDemoIncludedAccounts(formDemoIncludedAccountsInitValue);
    setFormDemoIncludedAccountChanges(formDemoIncludedAccountChangesInitValue);
  };
  const handleClickClose = () => {
    setOpen(false);
  };

  const [errorMaxSessions, setErrorMaxSessions] = useState(false);
  const [errorMinApplicationEquity, setErrorMinApplicationEquity] =
    useState(false);
  const [errorLiveIncludedAccounts, setErrorLiveIncludedAccounts] =
    useState(false);
  const [errorLiveIncludedAccountChanges, setErrorLiveIncludedAccountChanges] =
    useState(false);
  const [errorDemoIncludedAccounts, setErrorDemoIncludedAccounts] =
    useState(false);
  const [errorDemoIncludedAccountChanges, setErrorDemoIncludedAccountChanges] =
    useState(false);

  return ( stateStorage.userInformation.account_settings.config_data.config_data_distributor.manage_limitations === "1" ?
    <>
      <B3Tiny
        variant="outlined"
        onClick={handleClickOpen("paper")}
        sx={{ mb: 1 }}
        fullWidth
      >
        {t("distributorPortal.plan.actions.modifyLimitations")}
      </B3Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">
                {t("distributorPortal.plan.modifyDialog.title")}
              </Typography>

              <IconButton
                onClick={handleClickClose}
                sx={{ borderRadius: "10px" }}
              >
                <Iconify
                  icon="eva:close-outline"
                  minWidth={25}
                  minHeight={25}
                  color="#2E2E2E"
                />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                }}
              >
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="contentMiddle">
                      <Trans i18nKey="distributorPortal.plan.modifyDialog.body">
                        Use this dialog to modify your customer&apos;s plan limitations. You can either set a specific number, use the &apos;Plan default value,&apos; or set it to &apos;Unlimited.&apos;
                        <br />
                        <br />A modified value does not overwrite the plan standard value; it just sets a new value with higher priority. To restore the Plan default value, use the option &apos;Plan default value.&apos;
                      </Trans>
                    </Typography>
                  </Box>

                  <Box sx={{ pt: 2 }}>
                    <Typography variant="contentMiddleBold">
                      {t("distributorPortal.plan.sections.sessions")}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(
                            touched.permission_max_sessions &&
                              errors.permission_max_sessions
                          ) === true || errorMaxSessions === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(
                        touched.permission_max_sessions &&
                          errors.permission_max_sessions
                      ) === true || errorMaxSessions === true
                        ? t("distributorPortal.plan.fields.maxSessionsInvalid")
                        : t("distributorPortal.plan.fields.maxSessions")}
                    </Typography>
                    <Autocomplete
                      fullWidth
                      freeSolo
                      value={formMaxSessions}
                      size="small"
                      onInputChange={(event, value) => {
                        if (value === null) {
                          setErrorMaxSessions(false);
                          values.permission_max_sessions = "Plan default value";
                          setFormMaxSessions("Plan default value");
                        } else if (value !== "Plan default value") {
                          const isNumber = /^\d+$/.test(value);
                          if (isNumber === true) {
                            if (Number(value) < 0) {
                              setErrorMaxSessions(true);
                            } else {
                              setErrorMaxSessions(false);
                              values.permission_max_sessions = value;
                              setFormMaxSessions(value);
                            }
                          } else {
                            setErrorMaxSessions(true);
                          }
                        } else {
                          setErrorMaxSessions(false);
                          values.permission_max_sessions = value;
                          setFormMaxSessions(value);
                        }
                      }}
                      options={["Plan default value"]}
                      renderOption={(props, option) => (
                        <AutocompleteOption {...props} key={option}>
                          {option}
                        </AutocompleteOption>
                      )}
                      renderInput={(params) => (
                        <ModalContentTextField
                          {...params}
                          type="text"
                          {...getFieldProps("permission_max_sessions")}
                          error={Boolean(
                            touched.permission_max_sessions &&
                              errors.permission_max_sessions
                          )}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ pt: 2 }}>
                    <Typography variant="contentMiddleBold">
                      {t("distributorPortal.plan.sections.tradingLimits")}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(
                            touched.permission_min_application_equity &&
                              errors.permission_min_application_equity
                          ) === true || errorMinApplicationEquity === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(
                        touched.permission_min_application_equity &&
                          errors.permission_min_application_equity
                      ) === true || errorMinApplicationEquity === true
                        ? t("distributorPortal.plan.fields.minEquityInvalid")
                        : t("distributorPortal.plan.fields.minEquity")}
                    </Typography>
                    <Autocomplete
                      fullWidth
                      freeSolo
                      value={formMinApplicationEquity}
                      size="small"
                      onInputChange={(event, value) => {
                        if (value === null) {
                          setErrorMinApplicationEquity(false);
                          values.permission_min_application_equity =
                            "Plan default value";
                          setFormMinApplicationEquity("Plan default value");
                        } else if (
                          value !== "Plan default value" &&
                          value !== "Disabled"
                        ) {
                          const isNumber = /^\d+$/.test(value);
                          if (isNumber === true) {
                            if (Number(value) < 0) {
                              setErrorMinApplicationEquity(true);
                            } else {
                              setErrorMinApplicationEquity(false);
                              values.permission_min_application_equity = value;
                              setFormMinApplicationEquity(value);
                            }
                          } else {
                            setErrorMinApplicationEquity(true);
                          }
                        } else {
                          setErrorMinApplicationEquity(false);
                          values.permission_min_application_equity = value;
                          setFormMinApplicationEquity(value);
                        }
                      }}
                      options={["Plan default value", "Disabled"]}
                      renderOption={(props, option) => (
                        <AutocompleteOption {...props} key={option}>
                          {option}
                        </AutocompleteOption>
                      )}
                      renderInput={(params) => (
                        <ModalContentTextField
                          {...params}
                          type="text"
                          {...getFieldProps(
                            "permission_min_application_equity"
                          )}
                          error={Boolean(
                            touched.permission_min_application_equity &&
                              errors.permission_min_application_equity
                          )}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>

                  {!(
                    String(plan.live_included_accounts) === "100" &&
                    String(plan.demo_included_accounts) === "100"
                  ) ? (
                    <>
                  <Box sx={{ pt: 2 }}>
                    <Typography variant="contentMiddleBold">
                      {t("distributorPortal.plan.sections.liveAccounts")}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(
                            touched.permission_live_included_accounts &&
                              errors.permission_live_included_accounts
                          ) === true || errorLiveIncludedAccounts === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(
                        touched.permission_live_included_accounts &&
                          errors.permission_live_included_accounts
                      ) === true || errorLiveIncludedAccounts === true
                        ? t("distributorPortal.plan.fields.liveIncludedAccountsInvalid")
                        : t("distributorPortal.plan.fields.liveIncludedAccounts")}
                    </Typography>
                    <Autocomplete
                      fullWidth
                      freeSolo
                      value={formLiveIncludedAccounts}
                      size="small"
                      onInputChange={(event, value) => {
                        if (value === null) {
                          setErrorLiveIncludedAccounts(false);
                          values.permission_live_included_accounts =
                            "Plan default value";
                          setFormLiveIncludedAccounts("Plan default value");
                        } else if (
                          value !== "Plan default value" &&
                          value !== "Unlimited"
                        ) {
                          const isNumber = /^\d+$/.test(value);
                          if (isNumber === true) {
                            if (Number(value) < 0) {
                              setErrorLiveIncludedAccounts(true);
                            } else {
                              setErrorLiveIncludedAccounts(false);
                              values.permission_live_included_accounts = value;
                              setFormLiveIncludedAccounts(value);
                            }
                          } else {
                            setErrorLiveIncludedAccounts(true);
                          }
                        } else {
                          setErrorLiveIncludedAccounts(false);
                          values.permission_live_included_accounts = value;
                          setFormLiveIncludedAccounts(value);
                        }
                      }}
                      options={["Plan default value", "Unlimited"]}
                      renderOption={(props, option) => (
                        <AutocompleteOption {...props} key={option}>
                          {option}
                        </AutocompleteOption>
                      )}
                      renderInput={(params) => (
                        <ModalContentTextField
                          {...params}
                          type="text"
                          {...getFieldProps(
                            "permission_live_included_accounts"
                          )}
                          error={Boolean(
                            touched.permission_live_included_accounts &&
                              errors.permission_live_included_accounts
                          )}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(
                            touched.permission_live_included_account_changes &&
                              errors.permission_live_included_account_changes
                          ) === true || errorLiveIncludedAccountChanges === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(
                        touched.permission_live_included_account_changes &&
                          errors.permission_live_included_account_changes
                      ) === true || errorLiveIncludedAccountChanges === true
                        ? t("distributorPortal.plan.fields.liveIncludedAccountChangesInvalid")
                        : t("distributorPortal.plan.fields.liveIncludedAccountChanges")}
                    </Typography>

                    <Autocomplete
                      fullWidth
                      freeSolo
                      value={formLiveIncludedAccountChanges}
                      size="small"
                      onInputChange={(event, value) => {
                        if (value === null) {
                          setErrorLiveIncludedAccountChanges(false);
                          values.permission_live_included_account_changes =
                            "Plan default value";
                          setFormLiveIncludedAccountChanges(
                            "Plan default value"
                          );
                        } else if (
                          value !== "Plan default value" &&
                          value !== "Unlimited"
                        ) {
                          const isNumber = /^\d+$/.test(value);
                          if (isNumber === true) {
                            if (Number(value) < 0) {
                              setErrorLiveIncludedAccountChanges(true);
                            } else {
                              setErrorLiveIncludedAccountChanges(false);
                              values.permission_live_included_account_changes =
                                value;
                              setFormLiveIncludedAccountChanges(value);
                            }
                          } else {
                            setErrorLiveIncludedAccountChanges(true);
                          }
                        } else {
                          setErrorLiveIncludedAccountChanges(false);
                          values.permission_live_included_account_changes =
                            value;
                          setFormLiveIncludedAccountChanges(value);
                        }
                      }}
                      options={["Plan default value", "Unlimited"]}
                      renderOption={(props, option) => (
                        <AutocompleteOption {...props} key={option}>
                          {option}
                        </AutocompleteOption>
                      )}
                      renderInput={(params) => (
                        <ModalContentTextField
                          {...params}
                          type="text"
                          {...getFieldProps(
                            "permission_live_included_account_changes"
                          )}
                          error={Boolean(
                            touched.permission_live_included_account_changes &&
                              errors.permission_live_included_account_changes
                          )}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ pt: 2 }}>
                    <Typography variant="contentMiddleBold">
                      {t("distributorPortal.plan.sections.demoAccounts")}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(
                            touched.permission_demo_included_accounts &&
                              errors.permission_demo_included_accounts
                          ) === true || errorDemoIncludedAccounts === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(
                        touched.permission_demo_included_accounts &&
                          errors.permission_demo_included_accounts
                      ) === true || errorDemoIncludedAccounts === true
                        ? t("distributorPortal.plan.fields.demoIncludedAccountsInvalid")
                        : t("distributorPortal.plan.fields.demoIncludedAccounts")}
                    </Typography>
                    <Autocomplete
                      fullWidth
                      freeSolo
                      value={formDemoIncludedAccounts}
                      size="small"
                      onInputChange={(event, value) => {
                        if (value === null) {
                          setErrorDemoIncludedAccounts(false);
                          values.permission_demo_included_accounts =
                            "Plan default value";
                          setFormDemoIncludedAccounts("Plan default value");
                        } else if (
                          value !== "Plan default value" &&
                          value !== "Unlimited"
                        ) {
                          const isNumber = /^\d+$/.test(value);
                          if (isNumber === true) {
                            if (Number(value) < 0) {
                              setErrorDemoIncludedAccounts(true);
                            } else {
                              setErrorDemoIncludedAccounts(false);
                              values.permission_demo_included_accounts = value;
                              setFormDemoIncludedAccounts(value);
                            }
                          } else {
                            setErrorDemoIncludedAccounts(true);
                          }
                        } else {
                          setErrorDemoIncludedAccounts(false);
                          values.permission_demo_included_accounts = value;
                          setFormDemoIncludedAccounts(value);
                        }
                      }}
                      options={["Plan default value", "Unlimited"]}
                      renderOption={(props, option) => (
                        <AutocompleteOption {...props} key={option}>
                          {option}
                        </AutocompleteOption>
                      )}
                      renderInput={(params) => (
                        <ModalContentTextField
                          {...params}
                          type="text"
                          {...getFieldProps(
                            "permission_demo_included_accounts"
                          )}
                          error={Boolean(
                            touched.permission_demo_included_accounts &&
                              errors.permission_demo_included_accounts
                          )}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(
                            touched.permission_demo_included_account_changes &&
                              errors.permission_demo_included_account_changes
                          ) === true || errorDemoIncludedAccountChanges === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(
                        touched.permission_demo_included_account_changes &&
                          errors.permission_demo_included_account_changes
                      ) === true || errorDemoIncludedAccountChanges === true
                        ? t("distributorPortal.plan.fields.demoIncludedAccountChangesInvalid")
                        : t("distributorPortal.plan.fields.demoIncludedAccountChanges")}
                    </Typography>

                    <Autocomplete
                      fullWidth
                      freeSolo
                      value={formDemoIncludedAccountChanges}
                      size="small"
                      onInputChange={(event, value) => {
                        if (value === null) {
                          setErrorDemoIncludedAccountChanges(false);
                          values.permission_demo_included_account_changes =
                            "Plan default value";
                          setFormDemoIncludedAccountChanges(
                            "Plan default value"
                          );
                        } else if (
                          value !== "Plan default value" &&
                          value !== "Unlimited"
                        ) {
                          const isNumber = /^\d+$/.test(value);
                          if (isNumber === true) {
                            if (Number(value) < 0) {
                              setErrorDemoIncludedAccountChanges(true);
                            } else {
                              setErrorDemoIncludedAccountChanges(false);
                              values.permission_demo_included_account_changes =
                                value;
                              setFormDemoIncludedAccountChanges(value);
                            }
                          } else {
                            setErrorDemoIncludedAccountChanges(true);
                          }
                        } else {
                          setErrorDemoIncludedAccountChanges(false);
                          values.permission_demo_included_account_changes =
                            value;
                          setFormDemoIncludedAccountChanges(value);
                        }
                      }}
                      options={["Plan default value", "Unlimited"]}
                      renderOption={(props, option) => (
                        <AutocompleteOption {...props} key={option}>
                          {option}
                        </AutocompleteOption>
                      )}
                      renderInput={(params) => (
                        <ModalContentTextField
                          {...params}
                          type="text"
                          {...getFieldProps(
                            "permission_demo_included_account_changes"
                          )}
                          error={Boolean(
                            touched.permission_demo_included_account_changes &&
                              errors.permission_demo_included_account_changes
                          )}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>
                    </>
                  ) : null}
                </Stack>
              </DialogContent>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                <RequestMessage
                  wrapperRef={wrapperRef}
                  error={error}
                  warning={warning}
                  success={success}
                  returnMessage={returnMessage}
                />

                <ModalActionsLoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                >
                  {t("distributorPortal.common.modify")}
                </ModalActionsLoadingButton>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </> : null
  );
}
